import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Stack, StackItem } from '@astryxdesign/core/Stack'
import { Center } from '@astryxdesign/core/Center'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { Banner } from '@astryxdesign/core/Banner'
import { Spinner } from '@astryxdesign/core/Spinner'
import { Token } from '@astryxdesign/core/Token'
import { DateInput } from '@astryxdesign/core/DateInput'
import { Switch } from '@astryxdesign/core/Switch'
import { EmptyState } from '@astryxdesign/core/EmptyState'
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHeaderCell } from '@astryxdesign/core/Table'
import { readCourtOccupancy } from './consoleApi'
import {
    OCCUPANCY_KINDS,
    dayTitle,
    durationText,
    failureText,
    isStaffPerson,
    isoDay,
    kindMinutes,
    lastMonthRange,
    monthRange,
    normalizeOccupancy,
    occupancyCsv,
    occupancyFileName,
    occupancyRangeError,
    occupancyRangeTitle,
    parseIsoDay,
    personRoles,
    sameDay,
    shareText,
    todayParts,
} from './consoleText'

// "Occupation" (azure-zephyr-2): who held the club's courts over a range of
// days, and for what - at the end of the month with "Mois dernier", or on
// demand over any two dates. The server computes it from the bookings as they
// are; nothing is stored, so an edited block is counted as it is now.
//
// The courts first: hours open, hours held, and what held them. Then the
// people, most time first: the members put on each block, the coach of each
// séance, the players of each game - members of the roster first, a player
// who is not on it still counted as "Hors club". "Personnel seulement" keeps
// the club's staff: coaches, admins and Personnel (cosmic-cedar-3).
//
// The range is in the address ("?vue=occupation&du=2026-10-01&au=2026-10-31"),
// so a link sent to another admin opens on the same report.
// Wide enough for "Maintenance" and "373 h 30" on one line; the names take
// the rest.
const numberColumn = {
    width: 'calc(var(--spacing-10) * 2.75)',
    minWidth: 'calc(var(--spacing-10) * 2.75)',
    textAlign: 'end',
    whiteSpace: 'nowrap',
}

export default function CourtOccupancy({ clubId, clubName, range, onRangeChange, onSessionLost }) {
    const [load, setLoad] = useState({ status: 'loading' })
    const [staffOnly, setStaffOnly] = useState(false)
    const latest = useRef(0)

    const reload = useCallback(async () => {
        const request = ++latest.current
        setLoad({ status: 'loading' })
        let answer
        try {
            answer = await readCourtOccupancy(clubId, range.from, range.to)
        } catch (e) {
            answer = { status: 0 }
        }
        if (request !== latest.current) return
        if (answer.status === 200 && answer.json) {
            setLoad({ status: 'ready', report: normalizeOccupancy(answer.json) })
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            setLoad({ status: 'error', title: failureText(answer.status) })
        }
    }, [clubId, range, onSessionLost])

    useEffect(() => {
        reload()
    }, [reload])

    const today = todayParts()
    const thisMonth = monthRange(today)
    const lastMonth = lastMonthRange(today)
    const isRange = r => sameDay(r.from, range.from) && sameDay(r.to, range.to)

    // The dates as picked. One that would make a range the server refuses is
    // kept in its picker with the reason, and not sent: the report stays on
    // the last range that was.
    const [draft, setDraft] = useState(range)
    useEffect(() => setDraft(range), [range])
    const draftError = occupancyRangeError(draft.from, draft.to)
    const pick = (which, value) => {
        const picked = parseIsoDay(value)
        if (!picked) return
        const next = { ...draft, [which]: picked }
        setDraft(next)
        if (!occupancyRangeError(next.from, next.to)) onRangeChange(next)
    }
    const choose = r => onRangeChange(r)

    const exportCsv = () => {
        if (load.status !== 'ready') return
        const blob = new Blob([occupancyCsv(load.report, range.from, range.to)], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = occupancyFileName(range.from, range.to)
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
    }

    const dateInput = (which, label) => (
        <DateInput
            label={label}
            value={isoDay(draft[which])}
            weekStartsOn="mon"
            presentation="popover"
            format={value => {
                const picked = parseIsoDay(value)
                return picked ? dayTitle(picked) : value
            }}
            status={draftError && which === 'to' ? { type: 'error', message: draftError } : undefined}
            statusVariant="detached"
            width={260}
            onChange={value => pick(which, value)}
        />
    )

    const controls = (
        <Stack gap={2}>
            <Stack direction="horizontal" gap={3} vAlign="end" wrap="wrap">
                {dateInput('from', 'Du')}
                {dateInput('to', 'Au')}
                <Button label="Mois en cours" isDisabled={isRange(thisMonth)} onClick={() => choose(thisMonth)} />
                <Button label="Mois dernier" isDisabled={isRange(lastMonth)} onClick={() => choose(lastMonth)} />
                <StackItem size="fill" />
                <Button
                    label="Exporter CSV"
                    variant="secondary"
                    isDisabled={load.status !== 'ready'}
                    onClick={exportCsv}
                />
            </Stack>
        </Stack>
    )

    let body
    if (load.status === 'loading') {
        body = (
            <Center axis="both" padding={10}>
                <Spinner label="Calcul de l'occupation" />
            </Center>
        )
    } else if (load.status === 'error') {
        body = (
            <Stack gap={3} hAlign="start">
                <Banner status="error" title={load.title} />
                <Button label="Réessayer" variant="secondary" onClick={reload} />
            </Stack>
        )
    } else {
        const { report } = load
        const people = staffOnly ? report.people.filter(isStaffPerson) : report.people
        body = (
            <Stack gap={10}>
                <Stack gap={3}>
                    <Heading level={3}>Terrains</Heading>
                    <Text>
                        {report.courts === 0
                            ? 'Ce club n\'a aucun terrain.'
                            : `${durationText(report.heldMinutes)} occupées sur ${durationText(report.openMinutes)} d'ouverture (${shareText(report.heldMinutes, report.openMinutes)}), ${report.courts} terrain${report.courts > 1 ? 's' : ''}.`}
                    </Text>
                    <Table density="compact" dividers="rows">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Pour</TableHeaderCell>
                                <TableHeaderCell style={numberColumn}>Heures</TableHeaderCell>
                                <TableHeaderCell style={numberColumn}>Part</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {report.byKind.map(k => (
                                <TableRow key={k.value}>
                                    <TableCell><Text>{k.label}</Text></TableCell>
                                    <TableCell style={numberColumn}><Text hasTabularNumbers>{durationText(k.minutes)}</Text></TableCell>
                                    <TableCell style={numberColumn}><Text hasTabularNumbers color="secondary">{shareText(k.minutes, report.openMinutes)}</Text></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
                <Stack gap={3}>
                    <Stack direction="horizontal" gap={3} vAlign="center" hAlign="between" wrap="wrap">
                        <Heading level={3}>{`Personnes (${people.length})`}</Heading>
                        <Switch label="Personnel du club seulement" value={staffOnly} onChange={setStaffOnly} size="sm" />
                    </Stack>
                    {people.length === 0 ? (
                        <EmptyState
                            title={staffOnly ? 'Aucun membre du personnel sur cette période' : 'Personne sur cette période'}
                            description={staffOnly
                                ? 'Indiquez les intervenants en bloquant un créneau, ou marquez un adhérent comme Personnel dans l\'onglet Adhérents.'
                                : 'Aucune partie, aucun cours et aucun intervenant sur les terrains pendant cette période.'}
                        />
                    ) : (
                        <Table density="compact" dividers="rows">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>Nom</TableHeaderCell>
                                    <TableHeaderCell>Rôle</TableHeaderCell>
                                    <TableHeaderCell style={numberColumn}>Total</TableHeaderCell>
                                    {OCCUPANCY_KINDS.map(k => (
                                        <TableHeaderCell key={k.value} style={numberColumn}>{k.label}</TableHeaderCell>
                                    ))}
                                    <TableHeaderCell style={numberColumn}>Créneaux</TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {people.map(p => (
                                    <TableRow key={p.club_user_id || p.user_id}>
                                        <TableCell><Text weight="semibold">{p.name || 'Sans nom'}</Text></TableCell>
                                        <TableCell><PersonRoles person={p} /></TableCell>
                                        <TableCell style={numberColumn}>
                                            <Text hasTabularNumbers weight="semibold">{durationText(p.minutes)}</Text>
                                        </TableCell>
                                        {OCCUPANCY_KINDS.map(k => {
                                            const minutes = kindMinutes(p.by_kind, k.value)
                                            return (
                                                <TableCell key={k.value} style={numberColumn}>
                                                    <Text hasTabularNumbers color={minutes ? undefined : 'secondary'}>
                                                        {minutes ? durationText(minutes) : '—'}
                                                    </Text>
                                                </TableCell>
                                            )
                                        })}
                                        <TableCell style={numberColumn}><Text hasTabularNumbers>{p.bookings}</Text></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack gap={6}>
            <Stack gap={1}>
                <Heading level={2}>{`Occupation — ${occupancyRangeTitle(range.from, range.to)}`}</Heading>
                <Text color="secondary">{`Qui a occupé les terrains de ${clubName}, et pour quoi.`}</Text>
            </Stack>
            {controls}
            {body}
        </Stack>
    )
}

CourtOccupancy.propTypes = {
    clubId: PropTypes.string.isRequired,
    clubName: PropTypes.string,
    range: PropTypes.shape({
        from: PropTypes.object.isRequired,
        to: PropTypes.object.isRequired,
    }).isRequired,
    onRangeChange: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}

// The roles as tokens, "Hors club" for a player who is not on the roster,
// "Adhérent" for a member with none.
function PersonRoles({ person }) {
    if (!person.is_member) return <Token label="Hors club" color="gray" size="sm" />
    const roles = personRoles(person)
    if (roles.length === 0) return <Text color="secondary">Adhérent</Text>
    return (
        <Stack direction="horizontal" gap={1} wrap="wrap">
            {roles.map(role => (
                <StackItem key={role.label} size="static">
                    <Token label={role.label} color={role.color} size="sm" />
                </StackItem>
            ))}
        </Stack>
    )
}

PersonRoles.propTypes = {
    person: PropTypes.object.isRequired,
}
